import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TermsModal } from './TermsModal';
import { PrivacyModal } from './PrivacyModal';
interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
// Helper function to parse and format error messages
const getErrorMessage = (data: any, response: Response): string => {
  // Check for API response with CodigoRespuesta and DescripcionRespuesta
  if (data?.CodigoRespuesta || data?.DescripcionRespuesta) {
    const codigo = data.CodigoRespuesta;
    const descripcion = data.DescripcionRespuesta || '';
    // CodigoRespuesta "000" typically means success, anything else is an error
    if (codigo !== '000') {
      const lowerDesc = descripcion.toLowerCase();
      // Check for duplicate/already registered
      if (lowerDesc.includes('ya fue registrado') || lowerDesc.includes('ya registrado') || lowerDesc.includes('ya existe')) {
        return 'Este usuario ya está registrado en el sistema. Si necesitas ayuda, contacta con soporte.';
      }
      // Return the API description if available
      if (descripcion) {
        return descripcion;
      }
      // Fallback based on error code
      return `Error ${codigo}: No se pudo completar el registro.`;
    }
  }
  // Check for other common error message fields
  const apiMessage = data?.message || data?.error || data?.mensaje || data?.descripcion;
  if (apiMessage) {
    const lowerMessage = apiMessage.toLowerCase();
    // Check for duplicate/already registered errors
    if (lowerMessage.includes('ya existe') || lowerMessage.includes('ya registrado') || lowerMessage.includes('duplicado') || lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
      return 'Este usuario ya está registrado. Si olvidaste tu contraseña, contacta con soporte.';
    }
    // Check for invalid data errors
    if (lowerMessage.includes('inválido') || lowerMessage.includes('invalid') || lowerMessage.includes('formato incorrecto')) {
      return `Datos inválidos: ${apiMessage}`;
    }
    // Check for missing required fields
    if (lowerMessage.includes('requerido') || lowerMessage.includes('required') || lowerMessage.includes('obligatorio')) {
      return `Campo requerido: ${apiMessage}`;
    }
    // Return the original API message if it's user-friendly
    if (apiMessage.length < 150) {
      return apiMessage;
    }
  }
  // HTTP status-based messages
  if (response.status === 400) {
    return 'Los datos enviados son incorrectos. Por favor verifica la información.';
  }
  if (response.status === 409) {
    return 'Este usuario ya está registrado en el sistema.';
  }
  if (response.status === 500) {
    return 'Error en el servidor. Por favor intenta nuevamente en unos minutos.';
  }
  if (response.status === 503) {
    return 'El servicio no está disponible temporalmente. Intenta más tarde.';
  }
  // Default error message
  return 'Hubo un error al procesar tu registro. Por favor intenta nuevamente o contacta con soporte.';
};
export function RegistrationModal({
  isOpen,
  onClose
}: RegistrationModalProps) {
  const [formData, setFormData] = useState({
    idType: 'cedula',
    idNumber: '',
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    acceptTerms: false,
    isAdult: false
  });
  const [countryCode, setCountryCode] = useState('+593'); // Ecuador por defecto
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    // Document number validation based on type
    if (!formData.idNumber.trim()) {
      newErrors.idNumber = 'El número de documento es requerido';
    } else if (formData.idType === 'cedula') {
      // Cédula: exactly 10 numeric digits
      if (!/^\d{10}$/.test(formData.idNumber)) {
        newErrors.idNumber = 'La cédula debe tener exactamente 10 dígitos numéricos';
      }
    } else if (formData.idType === 'pasaporte') {
      // Pasaporte: 3-13 alphanumeric characters
      if (!/^[A-Za-z0-9]{3,13}$/.test(formData.idNumber)) {
        newErrors.idNumber = 'El pasaporte debe tener entre 3 y 13 caracteres alfanuméricos';
      }
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else if (!/^\d{9,10}$/.test(formData.phone)) {
      newErrors.phone = 'El teléfono debe tener 9 o 10 dígitos';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
    }
    if (!formData.isAdult) {
      newErrors.isAdult = 'Debes ser mayor de 18 años';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setShowError(false);
    try {
      const response = await fetch('https://admin.benefika.net/webservice/comprobante/api/RegistroUsuarioConvenio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authentication: 'e8047da76e25f654a4e571c22b7446686498357e039f65002967411ddb8d3b88'
        },
        body: JSON.stringify({
          TipoIdentificacion: formData.idType === 'cedula' ? 'CEDULA' : 'PASAPORTE',
          Cedula: formData.idNumber,
          Nombre: formData.firstName,
          Apellido: formData.lastName,
          Email: formData.email,
          Telefono: `${countryCode}${formData.phone}` // Include country code
        })
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Error al procesar la respuesta del servidor.');
      }
      // Log the response for debugging
      console.log('API Response:', {
        status: response.status,
        data
      });
      // Check for errors based on CodigoRespuesta
      // "000" = success, anything else = error
      const isSuccess = data.CodigoRespuesta === '000';
      if (!isSuccess) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Additional fallback checks for other error formats
      if (!response.ok || data.error || data.status === 'error' || data.success === false) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Show success message
      setShowSuccess(true);
      // Reset form
      setFormData({
        idType: 'cedula',
        idNumber: '',
        lastName: '',
        firstName: '',
        phone: '',
        email: '',
        acceptTerms: false,
        isAdult: false
      });
      setCountryCode('+593'); // Reset to Ecuador
      // Auto close after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error al registrar:', error);
      const message = error instanceof Error ? error.message : 'Hubo un error al procesar tu registro. Por favor intenta nuevamente.';
      setErrorMessage(message);
      setShowError(true);
      // Auto hide error after 8 seconds
      setTimeout(() => {
        setShowError(false);
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleIdTypeChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      idType: value,
      idNumber: '' // Clear document number when type changes
    }));
    // Clear document number error when type changes
    if (errors.idNumber) {
      setErrors(prev => ({
        ...prev,
        idNumber: ''
      }));
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-305">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative" data-id="element-306">
        {/* Error Message */}
        <AnimatePresence data-id="element-307">
          {showError && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="absolute top-0 left-0 right-0 z-40 p-4" data-id="element-308">
              <div className="bg-red-500/90 backdrop-blur-lg border border-red-400 rounded-lg p-4 flex items-start gap-3 shadow-lg" data-id="element-309">
                <AlertCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" data-id="element-310" />
                <div className="flex-1" data-id="element-311">
                  <h4 className="text-white font-bold mb-1" data-id="element-312">
                    Error en el registro
                  </h4>
                  <p className="text-white/90 text-sm leading-relaxed" data-id="element-313">
                    {errorMessage}
                  </p>
                </div>
                <button onClick={() => setShowError(false)} className="text-white/80 hover:text-white transition-colors" data-id="element-314">
                  <X className="w-4 h-4" data-id="element-315" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Success Message */}
        <AnimatePresence data-id="element-316">
          {showSuccess && <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="absolute inset-0 bg-emerald-600/95 backdrop-blur-lg z-50 flex flex-col items-center justify-center p-8 rounded-2xl" data-id="element-317">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} data-id="element-318">
                <CheckCircle className="w-24 h-24 text-white mb-6" data-id="element-319" />
              </motion.div>

              <motion.h3 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-3xl font-bold text-white mb-3 text-center" data-id="element-320">
                ¡Registro Exitoso!
              </motion.h3>

              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-xl text-white text-center" data-id="element-321">
                Bienvenido a ClubAhorra
              </motion.p>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="mt-8" data-id="element-322">
                <div className="flex gap-2 text-white text-sm" data-id="element-323">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-324"></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" style={{
                animationDelay: '0.2s'
              }} data-id="element-325"></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" style={{
                animationDelay: '0.4s'
              }} data-id="element-326"></div>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-327">
          <div data-id="element-328">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-329">Crear Cuenta</h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-330">
              Completa el formulario para unirte a ClubAhorra
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-50 rounded-lg" disabled={isSubmitting} data-id="element-331">
            <X className="h-5 w-5" data-id="element-332" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-333">
          {/* ID Type Selection */}
          <div data-id="element-334">
            <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-335">
              Tipo de Documento
            </label>
            <div className="flex gap-4" data-id="element-336">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-337">
                <input type="radio" name="idType" value="cedula" checked={formData.idType === 'cedula'} onChange={e => handleIdTypeChange(e.target.value)} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" disabled={isSubmitting} data-id="element-338" />
                <span className="text-gray-700" data-id="element-339">Cédula</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-340">
                <input type="radio" name="idType" value="pasaporte" checked={formData.idType === 'pasaporte'} onChange={e => handleIdTypeChange(e.target.value)} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" disabled={isSubmitting} data-id="element-341" />
                <span className="text-gray-700" data-id="element-342">Pasaporte</span>
              </label>
            </div>
          </div>

          {/* ID Number */}
          <div data-id="element-343">
            <label htmlFor="idNumber" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-344">
              {formData.idType === 'cedula' ? 'Número de Cédula' : 'Número de Pasaporte'}
            </label>
            <input type="text" id="idNumber" name="idNumber" required value={formData.idNumber} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${errors.idNumber ? 'border-red-500' : 'border-gray-200'}`} placeholder={formData.idType === 'cedula' ? '1234567890' : 'AB123456'} disabled={isSubmitting} data-id="element-345" />
            {errors.idNumber && <p className="text-red-500 text-sm mt-1" data-id="element-346">{errors.idNumber}</p>}
            <p className="text-gray-500 text-xs mt-1" data-id="element-347">
              {formData.idType === 'cedula' ? '10 dígitos numéricos' : '3 a 13 caracteres alfanuméricos'}
            </p>
          </div>

          {/* Last Name */}
          <div data-id="element-348">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-349">
              Apellido/s *
            </label>
            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${errors.lastName ? 'border-red-500' : 'border-gray-200'}`} placeholder="Ingresa tu apellido" disabled={isSubmitting} data-id="element-350" />
            {errors.lastName && <p className="text-red-500 text-sm mt-1" data-id="element-351">{errors.lastName}</p>}
          </div>

          {/* First Name */}
          <div data-id="element-352">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-353">
              Nombre/s *
            </label>
            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${errors.firstName ? 'border-red-500' : 'border-gray-200'}`} placeholder="Ingresa tu nombre" disabled={isSubmitting} data-id="element-354" />
            {errors.firstName && <p className="text-red-500 text-sm mt-1" data-id="element-355">{errors.firstName}</p>}
          </div>

          {/* Phone */}
          <div data-id="element-356">
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-357">
              Teléfono
            </label>
            <div className="flex gap-2" data-id="element-358">
              <select value={countryCode} onChange={e => setCountryCode(e.target.value)} className="w-32 px-3 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" disabled={isSubmitting} data-id="element-359">
                <option value="+593" data-id="element-360">🇪🇨 +593</option>
                <option value="+57" data-id="element-361">🇨🇴 +57</option>
                <option value="+51" data-id="element-362">🇵🇪 +51</option>
                <option value="+56" data-id="element-363">🇨🇱 +56</option>
                <option value="+54" data-id="element-364">🇦🇷 +54</option>
                <option value="+52" data-id="element-365">🇲🇽 +52</option>
                <option value="+1" data-id="element-366">🇺🇸 +1</option>
                <option value="+34" data-id="element-367">🇪🇸 +34</option>
              </select>
              <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${errors.phone ? 'border-red-500' : 'border-gray-200'}`} placeholder="987654321" disabled={isSubmitting} data-id="element-368" />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1" data-id="element-369">{errors.phone}</p>}
            <p className="text-gray-500 text-xs mt-1" data-id="element-370">
              9 o 10 dígitos sin el prefijo del país
            </p>
          </div>

          {/* Email */}
          <div data-id="element-371">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-372">
              Email *
            </label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-200'}`} placeholder="tu@email.com" disabled={isSubmitting} data-id="element-373" />
            {errors.email && <p className="text-red-500 text-sm mt-1" data-id="element-374">{errors.email}</p>}
          </div>

          {/* Terms */}
          <div data-id="element-375">
            <label className="flex items-start gap-3 cursor-pointer" data-id="element-376">
              <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" disabled={isSubmitting} data-id="element-377" />
              <span className="text-sm text-gray-600" data-id="element-378">
                Acepto los{' '}
                <button type="button" onClick={() => setShowTerms(true)} className="text-emerald-600 hover:text-emerald-700 font-medium underline" data-id="element-379">
                  términos y condiciones
                </button>{' '}
                y la{' '}
                <button type="button" onClick={() => setShowPrivacy(true)} className="text-emerald-600 hover:text-emerald-700 font-medium underline" data-id="element-380">
                  política de privacidad
                </button>
              </span>
            </label>
            {errors.acceptTerms && <p className="text-red-500 text-sm mt-1" data-id="element-381">{errors.acceptTerms}</p>}
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-600 text-white py-3.5 rounded-lg font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-382">
            {isSubmitting ? 'Registrando...' : 'Crear Cuenta'}
          </button>
        </form>
      </div>

      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} data-id="element-383" />
      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} data-id="element-384" />
    </div>;
}